/**
 * Heatmap Calculator
 * 
 * Calculates activity intensity per location for heatmap visualization.
 * Uses weighted scoring based on ActivityType with normalization to 0-100 scale.
 * 
 * Weighted Scoring:
 * - Corrections (In/Out): 1.5x weight (important for accuracy tracking)
 * - Pick/PutAway/Receiving: 1.0x weight (standard warehouse operations)
 * - Transfer: 0.5x weight (internal movements)
 * - Counting: 0.3x weight (periodic activity)
 * - Reservation: 0.2x weight (planning activity)
 * 
 * Normalization:
 * - 20+ weighted events = 100% intensity (maximum hotness)
 * - Linear scale from 0 to 20 weighted events
 * 
 * @author BizClick WMS
 * @version 1.28 - Fase 6 Implementation
 */

import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';
import { TimeWindow } from './ActivityCacheManager';

/**
 * Activity intensity for a single location
 */
export interface LocationIntensity {
  /** Location bin code */
  binCode: string;
  
  /** Intensity score (0-100) */
  intensity: number;
  
  /** Raw weighted event count (before normalization) */
  weightedEventCount: number;
  
  /** Total number of events (unweighted) */
  totalEvents: number;
  
  /** Breakdown by activity type */
  activityBreakdown: {
    [key in ActivityType]?: number;
  };
  
  /** Most frequent activity type */
  primaryActivityType?: ActivityType;
}

/**
 * Heatmap calculation result
 */
export interface HeatmapData {
  /** Intensity per location */
  locationIntensities: Map<string, LocationIntensity>;
  
  /** Maximum intensity found (for scaling) */
  maxIntensity: number;
  
  /** Minimum intensity found */
  minIntensity: number;
  
  /** Average intensity across all locations */
  averageIntensity: number;
  
  /** Time window used for calculation */
  timeWindow: TimeWindow;
  
  /** Total events analyzed */
  totalEvents: number;
  
  /** Calculation timestamp */
  calculatedAt: string;
}

/**
 * Heatmap calculation configuration
 */
export interface HeatmapConfig {
  /** Time window for filtering events */
  timeWindow: TimeWindow;
  
  /** Normalization threshold (events for 100% intensity) */
  normalizationThreshold: number;
  
  /** Include zero-activity locations in result */
  includeZeroActivity: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: HeatmapConfig = {
  timeWindow: TimeWindow.Today,
  normalizationThreshold: 20, // 20 weighted events = 100% intensity
  includeZeroActivity: true
};

/**
 * Activity type weights for intensity calculation
 */
const ACTIVITY_WEIGHTS: Record<ActivityType, number> = {
  [ActivityType.CorrectionOut]: 1.5,  // High importance
  [ActivityType.CorrectionIn]: 1.5,   // High importance
  [ActivityType.Pick]: 1.0,           // Standard
  [ActivityType.PutAway]: 1.0,        // Standard
  [ActivityType.Receiving]: 1.0,      // Standard
  [ActivityType.Transfer]: 0.5,       // Lower importance
  [ActivityType.Counting]: 0.3,       // Periodic
  [ActivityType.Reservation]: 0.2     // Planning
};

/**
 * Get weight for activity type
 */
export const getActivityWeight = (activityType: ActivityType): number => {
  return ACTIVITY_WEIGHTS[activityType] || 1.0;
};

/**
 * Heatmap Calculator
 * Calculates activity intensity for warehouse locations
 */
export class HeatmapCalculator {
  private config: HeatmapConfig;

  constructor(config: Partial<HeatmapConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Calculate heatmap from events
   * 
   * @param events - Array of location activity events
   * @param allBinCodes - Optional: Include all locations even with zero activity
   * @returns Heatmap data with intensity per location
   */
  public calculate(
    events: LocationActivityEvent[],
    allBinCodes?: string[]
  ): HeatmapData {
    const startTime = performance.now();
    
    // Group events by location
    const locationEvents = this.groupEventsByLocation(events);
    
    // Calculate intensity for each location
    const locationIntensities = new Map<string, LocationIntensity>();
    
    locationEvents.forEach((locationEvts, binCode) => {
      const intensity = this.calculateLocationIntensity(binCode, locationEvts);
      locationIntensities.set(binCode, intensity);
    });

    // Include zero-activity locations if requested
    if (this.config.includeZeroActivity && allBinCodes) {
      allBinCodes.forEach(binCode => {
        if (!locationIntensities.has(binCode)) {
          locationIntensities.set(binCode, {
            binCode,
            intensity: 0,
            weightedEventCount: 0,
            totalEvents: 0,
            activityBreakdown: {}
          });
        }
      });
    }

    // Calculate statistics
    const intensities = Array.from(locationIntensities.values()).map(loc => loc.intensity);
    const maxIntensity = intensities.length > 0 ? Math.max(...intensities) : 0;
    const minIntensity = intensities.length > 0 ? Math.min(...intensities) : 0;
    const averageIntensity = intensities.length > 0
      ? intensities.reduce((sum, val) => sum + val, 0) / intensities.length
      : 0;

    const executionTime = performance.now() - startTime;

    console.log(
      '[HeatmapCalculator] Calculated heatmap for',
      locationIntensities.size,
      'locations in',
      executionTime.toFixed(2),
      'ms'
    );

    return {
      locationIntensities,
      maxIntensity,
      minIntensity,
      averageIntensity,
      timeWindow: this.config.timeWindow,
      totalEvents: events.length,
      calculatedAt: new Date().toISOString()
    };
  }

  /**
   * Calculate intensity for a single location
   * 
   * @param binCode - Location bin code
   * @param events - Events for this location
   * @returns Location intensity data
   */
  private calculateLocationIntensity(
    binCode: string,
    events: LocationActivityEvent[]
  ): LocationIntensity {
    // Count events by activity type
    const activityBreakdown: { [key in ActivityType]?: number } = {};
    let weightedEventCount = 0;

    events.forEach(event => {
      const activityType = event.activityType;
      const weight = getActivityWeight(activityType);
      
      // Update breakdown
      activityBreakdown[activityType] = (activityBreakdown[activityType] || 0) + 1;
      
      // Add weighted count
      weightedEventCount += weight;
    });

    // Find primary (most frequent) activity type
    let primaryActivityType: ActivityType | undefined;
    let maxCount = 0;
    
    Object.entries(activityBreakdown).forEach(([type, count]) => {
      if (count > maxCount) {
        maxCount = count;
        primaryActivityType = Number(type) as ActivityType;
      }
    });

    // Normalize to 0-100 scale
    // Formula: intensity = min(100, (weightedEventCount / threshold) * 100)
    const intensity = Math.min(
      100,
      (weightedEventCount / this.config.normalizationThreshold) * 100
    );

    return {
      binCode,
      intensity: Math.round(intensity * 10) / 10, // Round to 1 decimal
      weightedEventCount,
      totalEvents: events.length,
      activityBreakdown,
      primaryActivityType
    };
  }

  /**
   * Group events by location
   * 
   * @param events - Array of events
   * @returns Map of binCode -> events
   */
  private groupEventsByLocation(
    events: LocationActivityEvent[]
  ): Map<string, LocationActivityEvent[]> {
    const grouped = new Map<string, LocationActivityEvent[]>();

    events.forEach(event => {
      const existing = grouped.get(event.binCode) || [];
      existing.push(event);
      grouped.set(event.binCode, existing);
    });

    return grouped;
  }

  /**
   * Update configuration
   * 
   * @param config - Partial configuration to update
   */
  public updateConfig(config: Partial<HeatmapConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('[HeatmapCalculator] Configuration updated:', this.config);
  }

  /**
   * Get current configuration
   * 
   * @returns Current configuration
   */
  public getConfig(): HeatmapConfig {
    return { ...this.config };
  }
}

/**
 * Get color for intensity value
 * 
 * Cold → Warm → Hot gradient:
 * - 0%: Light gray (no activity)
 * - 1-20%: Very light blue (minimal activity)
 * - 21-40%: Light blue (low activity)
 * - 41-60%: Yellow (moderate activity)
 * - 61-80%: Orange (high activity)
 * - 81-100%: Red (very high activity)
 * 
 * @param intensity - Intensity value (0-100)
 * @returns Tailwind color class
 */
export const getHeatmapColor = (intensity: number): string => {
  if (intensity === 0) return 'bg-gray-100'; // No activity
  if (intensity <= 20) return 'bg-blue-100'; // Very light blue
  if (intensity <= 40) return 'bg-blue-300'; // Light blue
  if (intensity <= 60) return 'bg-yellow-300'; // Yellow
  if (intensity <= 80) return 'bg-orange-400'; // Orange
  return 'bg-red-500'; // Red (hot)
};

/**
 * Get hex color for intensity value (for custom styling)
 * 
 * @param intensity - Intensity value (0-100)
 * @returns Hex color code
 */
export const getHeatmapColorHex = (intensity: number): string => {
  if (intensity === 0) return '#f3f4f6'; // Light gray
  if (intensity <= 20) return '#dbeafe'; // Very light blue
  if (intensity <= 40) return '#93c5fd'; // Light blue
  if (intensity <= 60) return '#fde68a'; // Yellow
  if (intensity <= 80) return '#fb923c'; // Orange
  return '#ef4444'; // Red
};

/**
 * Get border color for intensity value
 * 
 * @param intensity - Intensity value (0-100)
 * @returns Tailwind border color class
 */
export const getHeatmapBorderColor = (intensity: number): string => {
  if (intensity === 0) return 'border-gray-300';
  if (intensity <= 20) return 'border-blue-300';
  if (intensity <= 40) return 'border-blue-500';
  if (intensity <= 60) return 'border-yellow-500';
  if (intensity <= 80) return 'border-orange-500';
  return 'border-red-600';
};

/**
 * Get text color for intensity value (for readability)
 * 
 * @param intensity - Intensity value (0-100)
 * @returns Tailwind text color class
 */
export const getHeatmapTextColor = (intensity: number): string => {
  if (intensity <= 60) return 'text-gray-800'; // Dark text on light backgrounds
  return 'text-white'; // White text on dark backgrounds
};
